import { useState, useEffect, useCallback } from 'react'

type UseLocalStorageKeyDirectOptions = {
  /**
   * If true, defers the initial cache load to requestIdleCallback.
   * Useful when the data is not immediately needed (e.g., Apollo already has data).
   */
  deferLoad?: boolean
  /**
   * If true, skips loading from localStorage entirely.
   * Returns immediately with loading: false and data: undefined.
   */
  skip?: boolean
}

/**
 * A hook to use a key from localStorage with direct state management.
 *
 * Similar to useLocalForageKeyDirect but uses localStorage instead of IndexedDB:
 * - Synchronous reads (faster initial load, but blocks main thread briefly)
 * - 5MB total storage limit (shared across all keys)
 * - Data persists across sessions
 * - Updates state immediately on setData (optimistic update)
 * - Does not share state across components (each instance has its own state)
 *
 * Use this for smaller, frequently accessed cache entries where synchronous
 * access is preferred over IndexedDB's async nature.
 *
 * @param key - The key to read/write in localStorage
 * @param options - Optional configuration
 */
export const useLocalStorageKeyDirect = <T extends {}>(
  key: string,
  options?: UseLocalStorageKeyDirectOptions
) => {
  const [data, setDataState] = useState<T | undefined>(undefined)
  const [loading, setLoading] = useState(true)

  const skip = options?.skip ?? false

  // Read from localStorage on mount (or when key changes)
  useEffect(() => {
    // Skip loading entirely if skip option is enabled
    if (skip) {
      setDataState(undefined)
      setLoading(false)
      return
    }

    let idleHandle: number | undefined

    // deferLoad is intentionally not in deps - we only want to check it at mount/key-change,
    // not re-trigger loading if it changes mid-flight (e.g., Apollo returns data)
    const shouldDefer = options?.deferLoad ?? false

    const loadData = () => {
      try {
        const raw = localStorage.getItem(key)
        if (raw) {
          const parsed = JSON.parse(raw) as T
          setDataState(parsed)
        } else {
          setDataState(undefined)
        }
      } catch (err) {
        console.error('[localStorage] Failed to read/parse cache:', err)
        setDataState(undefined)
      }
      setLoading(false)
    }

    if (shouldDefer && typeof requestIdleCallback !== 'undefined') {
      // Defer loading to browser idle time for housekeeping operations
      idleHandle = requestIdleCallback(() => {
        loadData()
      })
    } else {
      // Load immediately - localStorage is synchronous
      loadData()
    }

    return () => {
      if (idleHandle !== undefined) {
        cancelIdleCallback(idleHandle)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, skip])

  // Write to localStorage and update state
  const setData = useCallback(
    (value: T) => {
      setDataState(value)
      try {
        localStorage.setItem(key, JSON.stringify(value))
      } catch (err) {
        if (err instanceof DOMException && err.name === 'QuotaExceededError') {
          console.warn('[localStorage] Storage quota exceeded, cache write failed for key:', key)
        } else {
          console.error('[localStorage] Failed to write cache:', err)
        }
      }
    },
    [key]
  )

  return {
    loading,
    data,
    setData,
    hasKey: !loading ? Boolean(data) : undefined,
  }
}
